/**
 * Modal Matcher Utility
 *
 * Matches URL paths against modal routes configuration.
 * Similar to react-router's path matching functionality.
 */

import type { ModalRoute, ModalRouteMatch } from "@/types/modal";
import { getModalRoutes } from "./modalRoutes";

/**
 * Path Matcher Class
 *
 * Handles URL pattern matching with parameters
 */
export class ModalMatcher {
  /**
   * Match a URL path against modal routes
   *
   * @param path - URL path to match (e.g., "/product/123")
   * @returns Match result or null if no route matches
   */
  static matchRoute(path: string): ModalRouteMatch | null {
    const routes = getModalRoutes();

    for (const route of routes) {
      const match = ModalMatcher.matchPath(path, route.path);
      if (match) {
        // Extract modal type from path (first segment)
        const modalType = ModalMatcher.extractModalType(path);

        return {
          route,
          params: match.params,
          modalType,
        };
      }
    }

    return null;
  }

  /**
   * Match a specific path pattern against a URL
   *
   * @param url - URL to match (e.g., "/product/123")
   * @param pattern - Pattern to match against (e.g., "/product/:id")
   * @returns Match result with parameters or null
   */
  private static matchPath(
    url: string,
    pattern: string
  ): { params: Record<string, string> } | null {
    // Convert pattern to regex
    const patternParts = pattern.split("/").filter(Boolean);
    const urlParts = url.split("/").filter(Boolean);

    // Must have same number of parts
    if (patternParts.length !== urlParts.length) {
      return null;
    }

    const params: Record<string, string> = {};

    // Check each part
    for (let i = 0; i < patternParts.length; i++) {
      const patternPart = patternParts[i];
      const urlPart = urlParts[i];

      if (patternPart.startsWith(":")) {
        // Parameter part
        const paramName = patternPart.slice(1);
        params[paramName] = decodeURIComponent(urlPart);
      } else {
        // Literal part - must match exactly
        if (patternPart !== urlPart) {
          return null;
        }
      }
    }

    return { params };
  }

  /**
   * Extract modal type from URL path
   *
   * @param path - URL path (e.g., "/product/123")
   * @returns Modal type (e.g., "product")
   */
  private static extractModalType(path: string): string {
    const parts = path.split("/").filter(Boolean);
    return parts[0] || "";
  }

  /**
   * Generate URL from route pattern and parameters
   *
   * @param pattern - Route pattern (e.g., "/product/:id")
   * @param params - Parameters to substitute
   * @returns Generated URL
   */
  static generateUrl(
    pattern: string,
    params: Record<string, string> = {}
  ): string {
    let url = pattern;

    // Replace parameters
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, encodeURIComponent(value));
    });

    return url;
  }

  /**
   * Check if a route exists for the given path
   *
   * @param path - Path to check
   * @returns True if route exists
   */
  static hasRoute(path: string): boolean {
    return ModalMatcher.matchRoute(path) !== null;
  }

  /**
   * Get all available route patterns
   *
   * @returns Array of route patterns
   */
  static getAllRoutePatterns(): string[] {
    return getModalRoutes().map((route) => route.path);
  }

  /**
   * Find route by modal type
   *
   * @param modalType - Modal type to find (e.g., "product")
   * @param withParams - Whether to include routes with parameters
   * @returns Found routes
   */
  static findRoutesByType(
    modalType: string,
    withParams: boolean = true
  ): ModalRoute[] {
    const routes = getModalRoutes();

    return routes.filter((route) => {
      const firstSegment = route.path.split("/").filter(Boolean)[0];
      const isCorrectType = firstSegment === modalType;

      if (!withParams) {
        const hasParams = route.path.includes(":");
        return isCorrectType && !hasParams;
      }

      return isCorrectType;
    });
  }
}

/**
 * Helper function for matching routes (for external use)
 */
export const matchModalRoute = (path: string): ModalRouteMatch | null => {
  return ModalMatcher.matchRoute(path);
};

/**
 * Helper function for generating URLs (for external use)
 */
export const generateModalUrl = (
  pattern: string,
  params: Record<string, string> = {}
): string => {
  return ModalMatcher.generateUrl(pattern, params);
};
